import React, { useRef, useState, useEffect } from 'react';

const ParcelleCarousel = ({ terrains = [] }) => {
    const scrollRef = useRef(null);
    const [openId, setOpenId] = useState(null);
    const [zoomedImg, setZoomedImg] = useState(null);

    // États pour la barre de progression
    const [scrollProgress, setScrollProgress] = useState(0); // Position du scroll en %
    const [thumbWidth, setThumbWidth] = useState(25); // Largeur du curseur en %

    // Fonction pour calculer la progression
    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

            // Calcul du pourcentage de progression
            // (Position actuelle / Espace total défilable) * 100
            const totalScrollable = scrollWidth - clientWidth;
            const progress = totalScrollable > 0 ? (scrollLeft / totalScrollable) * 100 : 0;

            setScrollProgress(progress);
        }
    };

    // Calculer la largeur initiale du curseur (optionnel, pour plus de précision)
    useEffect(() => {
        if (scrollRef.current) {
            const { scrollWidth, clientWidth } = scrollRef.current;
            // La largeur du curseur représente la proportion visible du contenu
            const width = (clientWidth / scrollWidth) * 100;
            setThumbWidth(width);
        }
    }, [terrains]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { clientWidth } = scrollRef.current;
            const scrollTo = direction === 'left'
                ? scrollRef.current.scrollLeft - clientWidth
                : scrollRef.current.scrollLeft + clientWidth;

            scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    };

    // Clic sur la carte : si l'overlay est déjà ouvert (hover ou clic précédent), on zoome.
    // Sinon (mobile sans hover), le clic se contente d'ouvrir l'overlay.
    const handleCardClick = (terrain) => {
        if (terrain.vendu) return;
        if (openId === terrain.id) {
            setZoomedImg(terrain.photo);
        } else {
            setOpenId(terrain.id);
        }
    };

    return (
        <div className="relative w-full group">

            {/* --- LIGHTBOX (Zoom) --- */}
            {zoomedImg && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1A1A1A]/90 backdrop-blur-sm cursor-zoom-out p-4 md:p-10"
                    onClick={() => setZoomedImg(null)}
                >
                    <button className="absolute top-10 right-10 text-[#F3F0E9] hover:rotate-90 transition-transform">
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <img src={zoomedImg} alt="Zoom" className="max-w-full max-h-full object-contain" />
                </div>
            )}

            {/* --- CAROUSEL --- */}
            <div
                ref={scrollRef}
                onScroll={handleScroll} // ON ÉCOUTE LE SCROLL ICI
                className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 md:gap-6 pb-4"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {terrains.map((terrain) => {
                    const isOpen = openId === terrain.id;

                    return (
                        <div
                            key={terrain.id}
                            className="min-w-[80%] sm:min-w-[45%] md:min-w-[calc(25%-1.125rem)] snap-center flex flex-col items-center"
                        >
                            {/* Image + overlay frosted glass (hover desktop / clic mobile, clic sur overlay ouvert = zoom) */}
                            <div
                                onMouseEnter={() => !terrain.vendu && setOpenId(terrain.id)}
                                onMouseLeave={() => !terrain.vendu && setOpenId((current) => (current === terrain.id ? null : current))}
                                onClick={() => handleCardClick(terrain)}
                                className={`relative w-full aspect-square overflow-hidden mb-4 md:mb-6 rounded-sm border border-[#1A1A1A]/15 ${terrain.vendu ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                                {/* Image de fond */}
                                <img
                                    src={terrain.photo}
                                    alt={terrain.nom}
                                    className={`absolute inset-0 w-full h-full object-cover ${terrain.vendu ? 'brightness-[0.4] grayscale' : ''}`}
                                />

                                {terrain.vendu && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-[#FF3B30] text-5xl md:text-7xl font-migra font-extrabold italic -rotate-12 tracking-tighter">
                                            Vendu
                                        </span>
                                    </div>
                                )}

                                {/* --- OVERLAY FROSTED GLASS --- */}
                                {!terrain.vendu && (
                                    <div className="absolute inset-0 flex flex-col justify-end overflow-hidden">
                                        {/* Fond flouté de l'image, qui remonte avec le panneau */}
                                        <div
                                            className="absolute inset-0 -z-10 transition-transform duration-500 ease-out"
                                            style={{
                                                backgroundImage: `url(${terrain.photo})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                filter: 'blur(10px)',
                                                opacity: 0.35,
                                                transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
                                            }}
                                        />

                                        {/* Panneau d'infos qui remonte par-dessus l'image */}
                                        <div
                                            className={`relative bg-[#F3F0E9] transition-transform duration-500 ease-out text-left px-5 pt-5 pb-10 md:px-6 md:pt-6 md:pb-12 ${isOpen ? 'cursor-zoom-in' : ''}`}
                                            style={{
                                                transform: isOpen ? 'translateY(0)' : 'translateY(calc(100% - 2.75rem))',
                                            }}
                                        >
                                            <h3 className="font-migra font-extrabold text-xl md:text-2xl italic text-[#1A1A1A] mb-2">
                                                {terrain.nom}
                                            </h3>
                                            {terrain.superficie && (
                                                <p className="text-xs md:text-sm uppercase tracking-widest font-medium text-[#1A1A1A]/70 mb-3">
                                                    {terrain.superficie} m²
                                                </p>
                                            )}
                                            <p className="text-sm md:text-base font-light leading-relaxed text-[#1A1A1A]/90">
                                                {terrain.description ??
                                                    "Parcelle exposée plein sud, en zone 4 façades, offrant une belle profondeur de jardin et un accès direct à la voirie."}
                                            </p>
                                        </div>

                                        {/* --- BANDE TOUJOURS VISIBLE --- */}
                                        <div className="absolute bottom-0 left-0 right-0 h-11 flex items-center justify-between px-5 md:px-6 bg-[#F3F0E9]">
                                            <span className="font-migra font-extrabold italic text-base md:text-lg text-[#1A1A1A]">
                                                {terrain.nom}
                                            </span>
                                            <span
                                                className="text-[#1A1A1A] text-lg transition-transform duration-500 ease-out"
                                                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                            >
                                                ^
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Boutons Navigation */}
            <button onClick={() => scroll('left')} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-[#F3F0E9]/80 backdrop-blur-sm p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-[#1A1A1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={() => scroll('right')} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-[#F3F0E9]/80 backdrop-blur-sm p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-[#1A1A1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>

            {/* --- BARRE DE PROGRESSION DYNAMIQUE --- */}
            <div className="flex justify-center mt-8">
                <div className="w-32 h-1 bg-[#1A1A1A]/10 rounded-full relative">
                    <div
                        className="absolute top-0 h-full bg-[#B4A782] rounded-full transition-all duration-150 ease-out"
                        style={{
                            width: `${thumbWidth}%`,
                            // On déplace le curseur en fonction du scroll
                            left: `${(scrollProgress * (100 - thumbWidth)) / 100}%`
                        }}
                    ></div>
                </div>
            </div>
        </div>
    );
};

export default ParcelleCarousel;